import React from 'react'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { fontSize, fontWeight, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { Heading } from '../Heading'
import { Text } from '../Text'
import { Stack } from '../Stack'
import { Button } from '../Button'
import { PageHeaderTrail } from './PageHeaderTrail'
import type { PageHeaderTrailItem, PageHeaderTrailLabels } from './PageHeaderTrail'

export type PageHeaderParent = PageHeaderTrailItem

export interface PageHeaderBackLink {
  label?: React.ReactNode
  onClick: () => void
}

export interface PageHeaderProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  /**
   * The levels between the side nav and this page, nearest last, ending in this page itself -
   * `[Dashboard, Leave setup, Locations, Berlin]` on a location.
   *
   * Second level and deeper only. A page the side nav links to directly already says where you
   * are, in the nav, and a trail there repeats it and then ends on the page's own title. An item
   * with no `to` is a level with no page behind it: a nav section, or this page.
   */
  parent?: PageHeaderParent[]
  /** Translations for the trail's two invisible strings - its landmark name and its overflow chip. */
  trailLabels?: Partial<PageHeaderTrailLabels>
  backLink?: PageHeaderBackLink
  /**
   * A status the whole page is in, drawn beside the title - "Default" on the default location. Kept
   * out of the h1 so the heading's name stays the page's name, and out of the trail, which repeats
   * the title as its last crumb and must not repeat this.
   */
  badge?: React.ReactNode
  /**
   * A picture of the page's subject - the person on a user page - drawn before the title block and
   * centred on all of it, the title and whatever sits under it. Sized by what you pass.
   */
  avatar?: React.ReactNode
  /**
   * What sits under the title when it is not a sentence: a person's labels on the user page. A
   * sentence is `subtitle`.
   */
  details?: React.ReactNode
  /** Right-aligned actions, e.g. primary/secondary Buttons. */
  actions?: React.ReactNode
}

/**
 * Vacation Tracker's page header: the title, whatever acts on the page, and - only where the side
 * nav cannot say it - the one or two levels above.
 *
 * The title is the page's h1, set at 22px semibold. The app used to draw it at 14px uppercase as an
 * h2, which left every page's outline starting at level 2 with no h1 above it - a page title that
 * was not the page's heading.
 *
 * The trail above the title is `PageHeaderTrail`: greys, a ghost back control, a rule below. It
 * draws only on a page under something - every trail this replaced opened on `Dashboard` (the
 * nav's first item), 18 of them carried a nav *section* label with no page behind it, and all 34
 * ended on the page's own title, repeated as a heading directly beneath.
 */
export function PageHeader({ title, subtitle, parent, trailLabels, backLink, badge, avatar, details, actions }: PageHeaderProps) {
  const hasTrail = Boolean(parent && parent.length > 0)

  return (
    // The header owns the space under itself. antd's Title used to supply it through its own
    // bottom margin, which the style above zeroes out - so without this the page body would start
    // directly under the title on every page at once.
    <Stack gap="xs" style={{ marginBottom: spacing.lg }}>
      {/* Above the title, on its own line. The trail says what this page is inside; the title says
          where you are. Beside each other they compete for the same glance, and the trail loses -
          which is the argument for putting the thing you read second, second. */}
      {hasTrail && parent && (
        // `current={title}` so the trail ends where you are: the page's own name, as plain text
        // rather than a link to itself. The title below repeats it: the trail is a
        // path and a path that stops one level short of its destination is a worse path.
        <div style={{ marginBottom: spacing.xs }}>
          <PageHeaderTrail items={parent} current={title} labels={trailLabels} />
        </div>
      )}
      <Stack direction="horizontal" align="center" justify="space-between" gap="lg" wrap>
        {/* minWidth 0 so a long title wraps its own words instead of being squeezed to a column
            narrower than one of them - at 375px "Departments" used to render over three lines. */}
        {/* 8px between the title and what sits under it, not 4: at 4px the title read as one block
            with the badges beneath it rather than as the name of the page. */}
        <Stack direction="horizontal" align="center" gap="base" style={{ minWidth: 0, flex: '1 1 auto' }}>
          {avatar && <span style={{ display: 'inline-flex', flex: 'none' }}>{avatar}</span>}
          <Stack gap="xs" style={{ minWidth: 0, flex: '1 1 auto' }}>
            {backLink && (
              <Button
                variant="text"
                icon={<ArrowLeftOutlined />}
                onClick={backLink.onClick}
                // A little further from the title than the subtitle is: leaving is a different kind
                // of thing from what the page is called, and equal gaps made the three read as a list.
                style={{ alignSelf: 'flex-start', paddingLeft: 0, marginBottom: spacing.xxs }}
              >
                {backLink.label}
              </Button>
            )}
            {/* The page's h1. It was an h2 rendered at 14px uppercase, matching the legacy
                `main-content-header-title` - which left every page starting its outline at level 2
                with no h1 above it. At 22px the uppercase treatment goes with it: small caps are an
                eyebrow convention and read as a banner at this size. */}
            <Stack direction="horizontal" align="center" gap="sm" wrap>
              <Heading
                level={1}
                style={{
                  fontSize: fontSize.pageTitle,
                  fontWeight: fontWeight.medium,
                  marginTop: 0,
                  marginBottom: 0,
                }}
              >
                {title}
              </Heading>
              {badge}
            </Stack>
            {subtitle && <Text tone="secondary">{subtitle}</Text>}
            {details}
          </Stack>
        </Stack>
        {actions && (
          <Stack direction="horizontal" gap="sm" wrap style={{ flexShrink: 0 }}>
            {actions}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default PageHeader
